import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { selectIsAuthenticated, selectClinicsLoading } from '@/store/slices/authSlice'

interface PublicRouteProps {
  children: React.ReactNode
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const clinicsLoading = useSelector(selectClinicsLoading)
  const [isInitializing, setIsInitializing] = useState(true)

  // Allow time for authentication state to be restored from sessionStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 100) // Short delay to allow HATEOASInitializer to restore auth state

    return () => clearTimeout(timer)
  }, [])

  // Show loading while still initializing auth state
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
          <p className="text-gray-600 font-lato">Initializing...</p>
        </div>
      </div>
    )
  }

  // If authenticated, redirect to dashboard
  // Note: AuthForm handles redirect to intended route after login
  if (isAuthenticated) {
    // If clinics are still loading, wait before redirecting
    if (clinicsLoading) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
            <p className="text-gray-600 font-lato">Loading clinic data...</p>
          </div>
        </div>
      )
    }
    
    // Check if there's an intended route in sessionStorage (for auto-login scenarios)
    const intendedRoute = sessionStorage.getItem('vitl_intended_route')
    const redirectTo = intendedRoute || '/dashboard'
    
    // Clear the intended route after using it
    if (intendedRoute) {
      sessionStorage.removeItem('vitl_intended_route')
      console.log('[PublicRoute] Auto-login redirect to intended route:', intendedRoute)
    }
    
    return <Navigate to={redirectTo} replace />
  }

  // User is not authenticated, show the public page (login/signup)
  return <>{children}</>
}

export default PublicRoute